/**
 * Machine page › Operators & shifts.
 *
 * Who drove this machine on each shift, and whether the machine's trouble
 * follows a person or the equipment — reviewer feedback (Paul Telford, 25 Sep
 * 2026): "tracking shifts and who is driving could be helpful to identify who
 * needs additional training."
 *
 * - **Verdict** — the root-cause call on the machine's flagged component, with
 *   the evidence lines and who it routes to.
 * - **Hours vs events** — each operator's share of the machine's hours beside
 *   their share of the component's events. Proportional bars are the
 *   equipment story; one operator's event bar far past their hours bar is the
 *   operator story.
 * - **Training** — any operator on this machine flagged by `trainingNeeds()`,
 *   linked to the training queue.
 * - **Shift log** — date, D/N, operator, crew, hours, events, codes.
 *
 * Data: `useReliability()` (telemetry, work orders, parts, live, RMS; the
 * attribution computed once per page). The shift split and most operators
 * are synthesised; the tab says so.
 *
 * UI layer.
 *
 * @see src/versions/v1/modules/fleet/MachineOperatorsTab.transform.ts
 * @see docs/logic/fleet/machine-operators.md
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Info } from 'lucide-react';
import { HelpAnchor, Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { useReliability } from '../../shared/reliability/useReliability';
import { TabLoader } from './TabLoader';
import { machineOperatorsView, type MachineOperatorsView } from './MachineOperatorsTab.transform';

export interface MachineOperatorsTabProps {
  machineId: string;
}

const CARD =
  'rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 backdrop-blur-lg';

export function MachineOperatorsTab({ machineId }: MachineOperatorsTabProps) {
  const { ctx, loading, error } = useReliability();
  const view = useMemo(() => (ctx ? machineOperatorsView(machineId, ctx) : null), [ctx, machineId]);

  return (
    <TabLoader loading={loading || !view} error={error} what="Operator attribution" rows={10}>
      {view ? <OperatorsBody view={view} /> : null}
    </TabLoader>
  );
}

function OperatorsBody({ view }: { view: MachineOperatorsView }) {
  if (!view.shifts.length) {
    return (
      <div className={CARD}>
        <p className="text-sm text-zinc-700 dark:text-zinc-300">
          {view.machine} has no operating hours in the 12-day window, so there are no shifts to
          attribute.
        </p>
      </div>
    );
  }

  return (
    <div className="@container space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted">
          <b className="text-zinc-900 dark:text-zinc-100">{view.totals.shifts}</b> shifts ·{' '}
          <b className="text-zinc-900 dark:text-zinc-100">{view.totals.hours}</b> h ·{' '}
          <b className="text-zinc-900 dark:text-zinc-100">{view.totals.operators}</b> operators ·{' '}
          <b className="text-zinc-900 dark:text-zinc-100">{view.totals.sensitiveEvents}</b>{' '}
          operator-sensitive events across all components · 29 Aug – 08 Sep 2026
        </p>
        <HelpAnchor doc="fleet/machine-operators" />
      </div>

      <div className="grid grid-cols-1 gap-4 @4xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <Commentable name="OperatorVerdict">
          <section className={CARD} aria-labelledby="op-verdict">
            <Verdict view={view} />
          </section>
        </Commentable>

        <Commentable name="OperatorShares">
          <section className={CARD} aria-labelledby="op-shares">
            <h3 id="op-shares" className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Share of hours vs share of events
            </h3>
            <p className="mt-0.5 text-[11px] text-muted">
              {view.shareScope} · bars in proportion means the events follow the hours
            </p>
            <ShareChart view={view} />
            {view.shareTakeaway ? (
              <p className="mt-4 border-t border-[var(--glass-border)] pt-3 text-xs text-zinc-700 dark:text-zinc-300">
                {view.shareTakeaway}
              </p>
            ) : null}
          </section>
        </Commentable>
      </div>

      {view.training.length ? (
        <Commentable name="OperatorTraining">
          <section
            className="rounded-2xl border border-[var(--warn)] bg-[var(--warn-soft)] p-4"
            aria-label="Training need"
          >
            {view.training.map((t) => (
              <div key={t.operatorId} className="flex flex-wrap items-start gap-3">
                <GraduationCap className="mt-0.5 h-5 w-5 shrink-0 text-[var(--warn)]" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                    Training need · {t.name}{' '}
                    <span className="font-normal text-muted">
                      crew {t.crew} · {t.ratio}× site peers
                    </span>
                  </p>
                  <p className="mt-0.5 text-xs text-zinc-700 dark:text-zinc-300">
                    <span className="mono">{t.ratePer100h.toFixed(1)}</span> events per 100 h
                    against <span className="mono">{t.peerRate.toFixed(1)}</span> for the site's
                    other operators of this class ({t.events} events over {t.hours.toFixed(1)} h,
                    all machines). {t.recommendation}
                  </p>
                </div>
                <Link
                  to="/issues/routing?tab=training"
                  className="shrink-0 self-center text-xs font-medium text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
                >
                  Open the training queue →
                </Link>
              </div>
            ))}
          </section>
        </Commentable>
      ) : null}

      <Commentable name="OperatorShiftLog">
        <section aria-labelledby="op-log" className="space-y-2">
          <h3 id="op-log" className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            Shift log <span className="font-normal text-muted">· who was driving, newest first</span>
          </h3>
          <Table>
            <THead>
              <TR>
                <TH>Date</TH>
                <TH>Shift</TH>
                <TH>Operator</TH>
                <TH>Crew</TH>
                <TH className="text-right">Hours</TH>
                {view.verdict ? (
                  <TH className="max-w-[9rem] whitespace-normal text-right">
                    {view.verdict.component} events
                  </TH>
                ) : null}
                <TH className="text-right">Sensitive events</TH>
                <TH>Codes</TH>
              </TR>
            </THead>
            <TBody>
              {view.shifts.map((s) => (
                <TR key={s.key}>
                  <TD className="mono whitespace-nowrap">{s.dateLabel}</TD>
                  <TD>
                    <ShiftChip shift={s.shift} />
                  </TD>
                  <TD className="whitespace-nowrap">
                    <span
                      className={cn(
                        s.isTopOperator && 'font-semibold text-zinc-900 dark:text-zinc-100',
                      )}
                    >
                      {s.operatorName}
                    </span>
                    {!s.synthesised ? (
                      <span className="ml-1.5 text-[10px] uppercase tracking-wider text-muted">
                        directory
                      </span>
                    ) : null}
                  </TD>
                  <TD className="mono">{s.crew}</TD>
                  <TD className="mono text-right">{s.hours.toFixed(1)}</TD>
                  {view.verdict ? (
                    <TD className="text-right">
                      <EventCount n={s.componentEvents} strong />
                    </TD>
                  ) : null}
                  <TD className="text-right">
                    <EventCount n={s.sensitiveEvents} />
                  </TD>
                  <TD className="mono text-[11px] text-muted">{s.codes || '—'}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </section>
      </Commentable>

      <Provenance view={view} />
    </div>
  );
}

function Verdict({ view }: { view: MachineOperatorsView }) {
  const v = view.verdict;
  if (!v) {
    return (
      <>
        <h3 id="op-verdict" className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
          Root cause
        </h3>
        <p className="mt-1.5 text-sm text-zinc-700 dark:text-zinc-300">
          Nothing on {view.machine} to classify: no component has operator-sensitive events, and
          none is in poor condition or worse.
        </p>
      </>
    );
  }
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id="op-verdict" className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
          Root cause · {v.component}
        </h3>
        <span className={`band-pill ${v.causeClass}`}>{v.causeLabel}</span>
      </div>
      <p className="mt-0.5 text-[11px] text-muted">
        Confidence <span className="mono">{v.confidencePct}%</span> · {v.events} operator-sensitive
        events{v.pValueLabel ? ` · concentration ${v.pValueLabel}` : ''} · routes to {v.routesTo}
      </p>
      <ul className="mt-3 space-y-1.5">
        {v.evidence.map((line) => (
          <li
            key={line}
            className="flex gap-2 text-xs leading-relaxed text-zinc-700 dark:text-zinc-300"
          >
            <span
              aria-hidden
              className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-[var(--ink-tertiary)]"
            />
            <span>{line}</span>
          </li>
        ))}
      </ul>
      {view.otherFindings.length ? (
        <p className="mt-3 border-t border-[var(--glass-border)] pt-2 text-[11px] text-muted">
          Also classified:{' '}
          {view.otherFindings.map((f, i) => (
            <span key={f.component}>
              {i ? ' · ' : ''}
              {f.component} <span className={`band-pill ${f.causeClass}`}>{f.causeLabel}</span>
            </span>
          ))}
        </p>
      ) : null}
    </>
  );
}

function ShareChart({ view }: { view: MachineOperatorsView }) {
  return (
    <div className="mt-3 space-y-3">
      <div className="flex gap-4 text-[11px] text-muted" aria-hidden>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-2 w-3 rounded-sm bg-[var(--ink-tertiary)] opacity-60" />
          hours
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-2 w-3 rounded-sm bg-[var(--info)]" />
          events
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-2 w-3 rounded-sm bg-[var(--crit)]" />
          events, concentrated
        </span>
      </div>
      <ul className="space-y-3">
        {view.shares.map((s) => (
          <li key={s.operatorId}>
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span
                className={cn(
                  'truncate text-zinc-900 dark:text-zinc-100',
                  s.concentrated && 'font-semibold',
                )}
              >
                {s.name} <span className="text-muted">· crew {s.crew}</span>
              </span>
              <span className="mono shrink-0 text-[11px] text-muted">
                {s.events} ev · {s.hours.toFixed(1)} h
                {s.ratio !== null && s.events ? ` · ${s.ratio}×` : ''}
              </span>
            </div>
            <Bar pct={s.hoursPct} className="bg-[var(--ink-tertiary)] opacity-60" label="hours" />
            <Bar
              pct={s.eventPct}
              className={s.concentrated ? 'bg-[var(--crit)]' : 'bg-[var(--info)]'}
              label="events"
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function Bar({ pct, className, label }: { pct: number; className: string; label: string }) {
  return (
    <div className="mt-1 flex items-center gap-2">
      <div
        className="h-2 flex-1 overflow-hidden rounded-sm bg-[var(--surface-3)]"
        role="img"
        aria-label={`${pct}% of ${label}`}
      >
        <div className={cn('h-full rounded-sm', className)} style={{ width: `${pct}%` }} />
      </div>
      <span className="mono w-9 shrink-0 text-right text-[11px] text-muted">{pct}%</span>
    </div>
  );
}

function ShiftChip({ shift }: { shift: 'D' | 'N' }) {
  return (
    <span
      title={shift === 'D' ? 'Day shift 06:00–18:00' : 'Night shift 18:00–06:00'}
      className={cn(
        'mono inline-flex h-5 w-5 items-center justify-center rounded text-[10px] font-semibold',
        shift === 'D'
          ? 'bg-[var(--warn-soft)] text-[var(--warn)]'
          : 'bg-[var(--info-soft)] text-[var(--info)]',
      )}
    >
      {shift}
    </span>
  );
}

function EventCount({ n, strong }: { n: number; strong?: boolean }) {
  if (!n) return <span className="mono text-muted">0</span>;
  return (
    <span
      className={cn(
        'mono inline-block min-w-[1.5rem] rounded px-1 text-center',
        strong && n >= 3
          ? 'bg-[var(--crit-soft)] font-semibold text-[var(--crit)]'
          : 'text-zinc-900 dark:text-zinc-100',
      )}
    >
      {n}
    </span>
  );
}

function Provenance({ view }: { view: MachineOperatorsView }) {
  return (
    <p className="flex gap-2 text-[11px] leading-relaxed text-muted">
      <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>
        Proof-of-concept provenance: the data pack records hours and events per machine-day with
        no shift and no operator. The day/night split and the roster are synthesised; every split
        sums back exactly to <span className="mono">shift_daily</span> and{' '}
        <span className="mono">events_daily</span>.{' '}
        {view.directoryOperators.length
          ? `${view.directoryOperators.join(', ')} ${view.directoryOperators.length === 1 ? 'is a' : 'are'} directory operator${view.directoryOperators.length === 1 ? '' : 's'}; `
          : ''}
        {view.synthesisedOperators} operator{view.synthesisedOperators === 1 ? ' is a' : 's are'}{' '}
        stand-in{view.synthesisedOperators === 1 ? '' : 's'}.
      </span>
    </p>
  );
}
